import type {
  CalendarFeedDto,
  Page,
  PractitionerDto,
  ServiceDto,
  TimeOffDto,
  WorkingHoursBlockDto,
} from "@clinic/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api } from "../api";
import { fieldErrors, hasFieldErrors } from "../forms";
import { formatDateTime, useT } from "../i18n";
import { canManage } from "../session";
import { hhmmToMinutes, minutesToHhmm } from "../time";
import { Alert, Badge, Button, Card, Field, PageHeader, Select, Spinner } from "../ui";
import { useClinic, useRequiredMe } from "./app";

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

function ServiceCheckboxes({ services, selected }: { services: ServiceDto[]; selected?: string[] }) {
  const { t } = useT();
  if (services.length === 0) return <p className="text-sm text-slate-500">{t("practitioners.no_services_hint")}</p>;
  return (
    <fieldset>
      <legend className="block text-sm font-medium text-slate-700">{t("practitioners.services")}</legend>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        {services.map((s) => (
          <label key={s.id} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm ring-1 ring-slate-200">
            <input type="checkbox" name="serviceIds" value={s.id} defaultChecked={selected?.includes(s.id)} />
            {s.name}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

const serviceIdsFrom = (form: HTMLFormElement) => new FormData(form).getAll("serviceIds").map(String);

export function PractitionersPage() {
  const me = useRequiredMe();
  const { t, tError } = useT();
  const qc = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [created, setCreated] = useState<PractitionerDto | null>(null);
  const list = useQuery({
    queryKey: ["practitioners", { showInactive }],
    queryFn: () => api<Page<PractitionerDto>>("GET", `/practitioners?limit=100&includeInactive=${showInactive}`),
  });
  const services = useQuery({
    queryKey: ["services", { showInactive: false }],
    queryFn: () => api<Page<ServiceDto>>("GET", "/services?limit=100"),
  });
  const create = useMutation({
    mutationFn: (input: unknown) => api<PractitionerDto>("POST", "/practitioners", input),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["practitioners"] });
      setCreated(p);
      setSelectedId(p.id);
    },
  });
  const fields = fieldErrors(create.error);
  const manage = canManage(me);
  // The freshly created practitioner opens for editing before the list has refetched.
  const selected = list.data?.items.find((p) => p.id === selectedId) ?? (created?.id === selectedId ? created : null);

  const onCreate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const v = Object.fromEntries(new FormData(form)) as Record<string, string>;
    create.mutate(
      { name: v.name, title: v.title ?? "", serviceIds: serviceIdsFrom(form) },
      { onSuccess: () => form.reset() },
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t("practitioners.title")} subtitle={t("practitioners.subtitle")}>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          {t("practitioners.show_inactive")}
        </label>
      </PageHeader>

      <Card className="p-0">
        {list.isPending ? (
          <div className="flex justify-center p-8 text-brand-700">
            <Spinner />
          </div>
        ) : list.isError ? (
          <div className="p-6">
            <Alert>{t("practitioners.load_failed")}</Alert>
          </div>
        ) : list.data.items.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500">{t("practitioners.empty")}</p>
        ) : (
          <ul className="divide-y divide-slate-200">
            {list.data.items.map((p) => {
              const days = new Set(p.workingHours.map((b) => b.weekday)).size;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(selectedId === p.id ? null : p.id)}
                    aria-expanded={selectedId === p.id}
                    className={`flex w-full flex-wrap items-center justify-between gap-3 px-6 py-4 text-left hover:bg-slate-50 ${
                      selectedId === p.id ? "bg-brand-50/50" : ""
                    }`}
                  >
                    <div>
                      <p className="font-medium">
                        {p.name}
                        {p.title ? <span className="ml-2 text-sm font-normal text-slate-500">{p.title}</span> : null}
                        {!p.active ? (
                          <span className="ml-2">
                            <Badge tone="gray">{t("practitioners.inactive")}</Badge>
                          </span>
                        ) : null}
                      </p>
                      <p className="text-sm text-slate-500">
                        {days === 0 ? t("practitioners.no_hours") : t("practitioners.hours_summary", { days })} ·{" "}
                        {p.serviceIds.length} {t("nav.services").toLowerCase()}
                      </p>
                    </div>
                    <span className="text-sm font-medium text-brand-700">
                      {selectedId === p.id ? t("common.close") : t("common.edit")}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {selected && services.data ? (
        <PractitionerEditor
          key={selected.id}
          practitioner={selected}
          services={services.data.items}
          readOnly={!manage}
        />
      ) : null}

      {manage ? (
        <Card>
          <h2 className="font-semibold">{t("practitioners.add_title")}</h2>
          <form className="mt-4 space-y-4" onSubmit={onCreate}>
            {create.error && !hasFieldErrors(create.error) ? <Alert>{tError(create.error)}</Alert> : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("practitioners.name")} name="name" required error={fields.name} />
              <Field
                label={t("practitioners.job_title")}
                name="title"
                placeholder={t("practitioners.job_title_placeholder")}
                error={fields.title}
              />
            </div>
            <ServiceCheckboxes services={services.data?.items ?? []} />
            {fields.serviceIds ? <p className="text-sm text-red-700">{fields.serviceIds}</p> : null}
            <Button busy={create.isPending}>{t("practitioners.add_button")}</Button>
          </form>
        </Card>
      ) : null}
    </div>
  );
}

function PractitionerEditor({
  practitioner,
  services,
  readOnly,
}: {
  practitioner: PractitionerDto;
  services: ServiceDto[];
  readOnly: boolean;
}) {
  const { t, tError } = useT();
  const qc = useQueryClient();
  const clinic = useClinic();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["practitioners"] });

  const update = useMutation({
    mutationFn: (input: unknown) => api<PractitionerDto>("PATCH", `/practitioners/${practitioner.id}`, input),
    onSuccess: invalidate,
  });
  const saveHours = useMutation({
    mutationFn: (blocks: WorkingHoursBlockDto[]) =>
      api<PractitionerDto>("PUT", `/practitioners/${practitioner.id}/working-hours`, { blocks }),
    onSuccess: invalidate,
  });
  const [blocks, setBlocks] = useState<WorkingHoursBlockDto[]>(practitioner.workingHours);
  const updateBlock = (i: number, patch: Partial<WorkingHoursBlockDto>) =>
    setBlocks((prev) => prev.map((b, j) => (j === i ? { ...b, ...patch } : b)));

  const detailFields = fieldErrors(update.error);
  const hoursError = saveHours.error ? (fieldErrors(saveHours.error).blocks ?? tError(saveHours.error)) : null;

  return (
    <div className="space-y-6">
      <Card>
        <h2 className="font-semibold">{t("practitioners.edit_title")}</h2>
        <form
          className="mt-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const v = Object.fromEntries(new FormData(form)) as Record<string, string>;
            update.mutate({
              name: v.name,
              title: v.title ?? "",
              active: v.active === "on",
              serviceIds: serviceIdsFrom(form),
            });
          }}
        >
          {update.isSuccess ? <Alert tone="success">{t("practitioners.saved")}</Alert> : null}
          {update.error && !hasFieldErrors(update.error) ? <Alert>{tError(update.error)}</Alert> : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t("practitioners.name")}
              name="name"
              defaultValue={practitioner.name}
              required
              disabled={readOnly}
              error={detailFields.name}
            />
            <Field
              label={t("practitioners.job_title")}
              name="title"
              defaultValue={practitioner.title}
              disabled={readOnly}
              error={detailFields.title}
            />
          </div>
          <ServiceCheckboxes services={services} selected={practitioner.serviceIds} />
          {detailFields.serviceIds ? <p className="text-sm text-red-700">{detailFields.serviceIds}</p> : null}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" name="active" defaultChecked={practitioner.active} disabled={readOnly} />
            {t("practitioners.active")}
          </label>
          {!readOnly ? <Button busy={update.isPending}>{t("common.save")}</Button> : null}
        </form>
      </Card>

      <Card>
        <h2 className="font-semibold">{t("practitioners.working_hours")}</h2>
        <p className="mt-1 text-sm text-slate-600">
          {t("practitioners.working_hours_hint", { tz: clinic.data?.timezone ?? "" })}
        </p>
        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            saveHours.mutate(blocks);
          }}
        >
          {saveHours.isSuccess ? <Alert tone="success">{t("practitioners.hours_saved")}</Alert> : null}
          {hoursError ? <Alert>{hoursError}</Alert> : null}
          {blocks.length === 0 ? <p className="text-sm text-slate-500">{t("practitioners.no_hours")}</p> : null}
          {blocks.map((b, i) => (
            // Rows have no stable identity beyond their position; index keys are fine for this small editor.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
            <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] items-end gap-2">
              <Select
                label={t("bookings.date")}
                value={b.weekday}
                disabled={readOnly}
                onChange={(e) => updateBlock(i, { weekday: Number(e.target.value) })}
              >
                {WEEKDAYS.map((d) => (
                  <option key={d} value={d}>
                    {t(`weekday.${d}`)}
                  </option>
                ))}
              </Select>
              <Field
                label={t("common.start")}
                type="time"
                step={300}
                value={minutesToHhmm(b.startMinute)}
                disabled={readOnly}
                onChange={(e) => updateBlock(i, { startMinute: hhmmToMinutes(e.target.value) })}
              />
              <Field
                label={t("common.end")}
                type="time"
                step={300}
                value={minutesToHhmm(b.endMinute === 1440 ? 1439 : b.endMinute)}
                disabled={readOnly}
                onChange={(e) => updateBlock(i, { endMinute: hhmmToMinutes(e.target.value) })}
              />
              {!readOnly ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setBlocks((prev) => prev.filter((_, j) => j !== i))}
                >
                  {t("common.remove")}
                </Button>
              ) : (
                <span />
              )}
            </div>
          ))}
          {!readOnly ? (
            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setBlocks((prev) => [...prev, { weekday: 1, startMinute: 9 * 60, endMinute: 17 * 60 }])}
              >
                {t("practitioners.add_block")}
              </Button>
              <Button busy={saveHours.isPending}>{t("practitioners.save_hours")}</Button>
            </div>
          ) : null}
        </form>
      </Card>

      <TimeOffCard practitionerId={practitioner.id} readOnly={readOnly} />
      {!readOnly ? <CalendarFeedCard practitioner={practitioner} /> : null}
    </div>
  );
}

function CalendarFeedCard({ practitioner }: { practitioner: PractitionerDto }) {
  const { t, tError } = useT();
  const qc = useQueryClient();
  const [url, setUrl] = useState<string | null>(null);
  const issue = useMutation({
    mutationFn: () => api<CalendarFeedDto>("POST", `/practitioners/${practitioner.id}/calendar-feed`),
    onSuccess: (feed) => {
      setUrl(feed.url);
      qc.invalidateQueries({ queryKey: ["practitioners"] });
    },
  });
  const revoke = useMutation({
    mutationFn: () => api<void>("DELETE", `/practitioners/${practitioner.id}/calendar-feed`),
    onSuccess: () => {
      setUrl(null);
      qc.invalidateQueries({ queryKey: ["practitioners"] });
    },
  });
  const error = issue.error ?? revoke.error;
  return (
    <Card>
      <h2 className="font-semibold">{t("calendar.title")}</h2>
      <p className="mt-1 text-sm text-slate-600">{t("calendar.text")}</p>
      {error ? (
        <div className="mt-3">
          <Alert>{tError(error)}</Alert>
        </div>
      ) : null}
      {url ? (
        <div className="mt-4 space-y-2">
          <Alert tone="success">{t("calendar.issued")}</Alert>
          <Field label="URL" value={url} readOnly onFocus={(e) => e.currentTarget.select()} />
        </div>
      ) : practitioner.hasCalendarFeed ? (
        <p className="mt-3 text-sm text-slate-700">{t("calendar.active")}</p>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          type="button"
          variant={practitioner.hasCalendarFeed ? "secondary" : "primary"}
          busy={issue.isPending}
          onClick={() => issue.mutate()}
        >
          {practitioner.hasCalendarFeed ? t("calendar.reissue") : t("calendar.issue")}
        </Button>
        {practitioner.hasCalendarFeed ? (
          <Button type="button" variant="ghost" busy={revoke.isPending} onClick={() => revoke.mutate()}>
            {t("calendar.revoke")}
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

function TimeOffCard({ practitionerId, readOnly }: { practitionerId: string; readOnly: boolean }) {
  const { t, tError, locale } = useT();
  const clinic = useClinic();
  const qc = useQueryClient();
  const from = new Date();
  from.setUTCHours(0, 0, 0, 0);
  const to = new Date(from.getTime() + 90 * 86_400_000);
  const key = ["time-off", practitionerId];
  const list = useQuery({
    queryKey: key,
    queryFn: () =>
      api<{ items: TimeOffDto[] }>(
        "GET",
        `/practitioners/${practitionerId}/time-off?from=${from.toISOString()}&to=${to.toISOString()}`,
      ),
  });
  const add = useMutation({
    mutationFn: (input: unknown) => api<TimeOffDto>("POST", `/practitioners/${practitionerId}/time-off`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api<void>("DELETE", `/practitioners/${practitionerId}/time-off/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
  const fields = fieldErrors(add.error);

  return (
    <Card>
      <h2 className="font-semibold">{t("practitioners.time_off")}</h2>
      <p className="mt-1 text-sm text-slate-600">{t("practitioners.time_off_hint")}</p>
      <div className="mt-4">
        {list.isPending ? (
          <Spinner />
        ) : list.isError ? (
          <Alert>{tError(list.error)}</Alert>
        ) : list.data.items.length === 0 ? (
          <p className="text-sm text-slate-500">{t("practitioners.time_off_empty")}</p>
        ) : (
          <ul className="divide-y divide-slate-200">
            {list.data.items.map((o) => (
              <li key={o.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                <span>
                  {formatDateTime(locale, o.startsAt, clinic.data?.timezone)} →{" "}
                  {formatDateTime(locale, o.endsAt, clinic.data?.timezone)}
                  {o.reason ? <span className="ml-2 text-slate-500">{o.reason}</span> : null}
                </span>
                {!readOnly ? (
                  <Button
                    type="button"
                    variant="ghost"
                    busy={remove.isPending && remove.variables === o.id}
                    onClick={() => remove.mutate(o.id)}
                  >
                    {t("common.remove")}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
      {!readOnly ? (
        <form
          className="mt-4 grid gap-4 sm:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const v = Object.fromEntries(new FormData(form)) as Record<string, string>;
            add.mutate(
              {
                startsAt: new Date(v.startsAt ?? "").toISOString(),
                endsAt: new Date(v.endsAt ?? "").toISOString(),
                reason: v.reason ?? "",
              },
              { onSuccess: () => form.reset() },
            );
          }}
        >
          {add.isSuccess ? (
            <div className="sm:col-span-3">
              <Alert tone="success">{t("practitioners.time_off_added")}</Alert>
            </div>
          ) : null}
          {add.error && !hasFieldErrors(add.error) ? (
            <div className="sm:col-span-3">
              <Alert>{tError(add.error)}</Alert>
            </div>
          ) : null}
          <Field label={t("common.start")} name="startsAt" type="datetime-local" required error={fields.startsAt} />
          <Field label={t("common.end")} name="endsAt" type="datetime-local" required error={fields.endsAt} />
          <Field label={t("practitioners.time_off_reason")} name="reason" error={fields.reason} />
          <div className="sm:col-span-3">
            <Button busy={add.isPending}>{t("practitioners.add_time_off")}</Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}
